// src/managers/PinManager.js
import { getFirestore, doc, setDoc, deleteDoc, collection, getDocs, Timestamp, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app } from '@/firebase/config';
import { authManager } from '@/managers/AuthManager';

// --- Constants ---
export const PIN_TYPES = {
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
    WORKSPACE_FILE: 'WORKSPACE_FILE',
};

// --- Firestore & Storage Setup ---
const firestore = getFirestore(app);
const storage = getStorage(app);

// --- File Processing Utilities ---

/* ========================
 * processTextFile - Reads a text file and extracts content and metadata.
 * ========================*/
export const processTextFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            resolve({
                type: 'text',
                content,
                size: file.size,
            });
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
    });
};

/* ========================
 * processImage - Optimizes and converts an image to WebP format.
 * ========================*/
export const processImage = async (file) => {
    if (!file.type.startsWith('image/')) {
        throw new Error('File is not an image');
    }
    if (file.type === 'image/webp') {
        return file; // Already optimized
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const maxDimension = 2048;
            let { width, height } = img;

            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
                if (!blob) reject(new Error('Failed to create WebP blob'));
                const optimizedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
                    type: 'image/webp'
                });
                resolve(optimizedFile);
            }, 'image/webp', 0.85);
        };
        img.onerror = (err) => reject(err);
        img.src = URL.createObjectURL(file);
    });
};

// --- PinManager Class ---

class PinManager {
    constructor() {
        this.subscribers = new Set();
        this.state = {
            pins: [],
            isLoading: true,
            error: null,
            isInitialized: false,
            activeConversationPins: new Set()
        };
    }

    setState(newState) {
        this.state = { ...this.state, ...newState };
        this.notifySubscribers();
    }

    getState() {
        return this.state;
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        callback(this.state);
        return () => this.subscribers.delete(callback);
    }

    notifySubscribers() {
        this.subscribers.forEach(callback => callback(this.state));
    }

    isActive(pinId) {
        return this.state.activeConversationPins.has(pinId);
    }

    activatePin(pinId) {
        const newActiveSet = new Set(this.state.activeConversationPins);
        newActiveSet.add(pinId);
        this.setState({ activeConversationPins: newActiveSet });
    }

    deactivatePin(pinId) {
        const newActiveSet = new Set(this.state.activeConversationPins);
        newActiveSet.delete(pinId);
        this.setState({ activeConversationPins: newActiveSet });
    }

    getActivePins() {
        return this.state.pins.filter(pin => this.isActive(pin.id));
    }

    generatePinId() {
        return `pin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    async uploadToStorage(file, pinId) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) throw new Error('Authentication required');
        
        const uniqueFileName = `${Date.now()}-${file.name}`;
        const path = `users/${user.uid}/pins/${pinId}/${uniqueFileName}`;
        
        const storageRefInstance = ref(storage, path);
        const snapshot = await uploadBytes(storageRefInstance, file);
        return await getDownloadURL(snapshot.ref);
    }

    async refreshFromDB() {
        this.setState({ isLoading: true, error: null });
        const user = authManager.getCurrentUser();
        if (!user?.uid) {
            return this.setState({ pins: [], isLoading: false, isInitialized: true });
        }

        try {
            const pinsRef = collection(firestore, 'users', user.uid, 'pins');
            const pinsSnapshot = await getDocs(pinsRef);
            const loadedPins = pinsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            this.setState({ pins: loadedPins, isLoading: false, isInitialized: true });
        } catch (err) {
            this.setState({ error: err.message, isLoading: false, isInitialized: true });
        }
    }

    async createPin(pinData) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) throw new Error('Authentication required');

        const pinId = pinData.id || this.generatePinId();
        const timestamp = Timestamp.now();
        const newPin = {
            ...pinData,
            id: pinId,
            userId: user.uid,
            created: timestamp,
            modified: timestamp,
        };

        const pinRef = doc(firestore, 'users', user.uid, 'pins', pinId);
        await setDoc(pinRef, newPin);

        this.setState({ pins: [...this.state.pins, newPin] });
        return newPin;
    }

    async updatePin(pinId, updates) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) throw new Error('Authentication required');

        const pinRef = doc(firestore, 'users', user.uid, 'pins', pinId);
        const updateData = { ...updates, modified: Timestamp.now() };
        await updateDoc(pinRef, updateData);

        this.setState({
            pins: this.state.pins.map(pin => 
                pin.id === pinId ? { ...pin, ...updateData } : pin
            )
        });
    }

    async deletePin(pinId) {
        const user = authManager.getCurrentUser();
        if (!user?.uid) throw new Error('Authentication required');

        this.deactivatePin(pinId);
        
        const pinToDelete = this.state.pins.find(p => p.id === pinId);
        if (pinToDelete?.storageUrl) {
            try {
                const fileRef = ref(storage, pinToDelete.storageUrl);
                await deleteObject(fileRef);
            } catch (error) {
                if (error.code !== 'storage/object-not-found') {
                    console.error("Error deleting storage object:", error);
                }
            }
        }
        
        const pinRef = doc(firestore, 'users', user.uid, 'pins', pinId);
        await deleteDoc(pinRef);

        this.setState({ pins: this.state.pins.filter(p => p.id !== pinId) });
    }

    async clearLibrary() {
        this.setState({ isLoading: true });
        const allPins = [...this.state.pins];
        // Clear local state immediately for better UX
        this.setState({ pins: [], activeConversationPins: new Set() });

        try {
            // Asynchronously delete all pins
            await Promise.all(allPins.map(pin => this.deletePin(pin.id)));
        } catch (err) {
            // If something fails, refresh from the server to get the correct state
            await this.refreshFromDB();
            throw err;
        } finally {
            this.setState({ isLoading: false });
        }
    }
}

export const pinManager = new PinManager();